import moment from 'moment';

import { TierType } from 'global-services/constants/OhsObject';
import { getWorkplaceOptions } from 'user/OhsUserServices';
import { OhsFilterList } from 'dashboard/topFilter/OhsFilterModels';
import { IncidentInjuredPersonRoleList } from 'user/configurations/incident/OhsIncidentConfig';
import OhsIncidentRecord from 'incident/OhsIncidentRecord';

import {
  incidentsLtiRecord,
  OhsIncidentLtiRecord,
  OhsIncidentLtiRecordResponse,
  OhsltiWorkplace,
  TransformedLtiHoursData,
} from './OhsIncidentModels';
import { OhsIncidentLtiServices } from './OhsIncidentLtiServices';

// Reusable function to generate default LTI records for a list of years and roles
function genDefaultIncidentLtiLists(years: number[], roles: string[]): any[] {
  const defaultIncidentLtiLists: any[] = [];
  years.forEach((year: number, index: number) => {
    roles.forEach((role: string, roleId: number) => {
      defaultIncidentLtiLists.push({
        ...incidentsLtiRecord,
        id: index * roles.length + roleId,
        year: String(year),
        role,
      });
    });
  });
  return defaultIncidentLtiLists;
}

const genLast2yearsDefaultList = (userRoleList: string[]) => {
  const years = [moment().year(), moment().subtract(1, 'year').year()];
  return genDefaultIncidentLtiLists(years, userRoleList);
};

const genOldestYearList = (
  oldestYearCurrentIncidentLtiLists: number,
  userRoleList: string[],
  oldestYear: number
) => {
  const years = Array.from(
    { length: oldestYearCurrentIncidentLtiLists - oldestYear },
    (_, index) => oldestYearCurrentIncidentLtiLists - 1 - index
  );
  return genDefaultIncidentLtiLists(years, userRoleList);
};

type InputData = {
  _id: string;
  year: number;
  role?: string;
  hours: Record<string, number>;
};

const transformData = (data: InputData[]): OhsIncidentLtiRecordResponse[] => {
  const monthMap: Record<string, string> = {
    Jan: 'jan',
    Feb: 'feb',
    Mar: 'mar',
    Apr: 'apr',
    May: 'may',
    Jun: 'jun',
    Jul: 'jul',
    Aug: 'aug',
    Sep: 'sep',
    Oct: 'oct',
    Nov: 'nov',
    Dec: 'dec',
  };

  return data.map((item) => {
    const transformed: any = {
      id: item._id,
      year: item.year.toString(),
      role: item.role || '',
      jan: null,
      feb: null,
      mar: null,
      apr: null,
      may: null,
      jun: null,
      jul: null,
      aug: null,
      sep: null,
      oct: null,
      nov: null,
      dec: null,
    };

    Object.entries(item.hours).forEach(([month, value]) => {
      const key = monthMap[month];
      if (key) {
        transformed[key] = value;
      }
    });

    return transformed;
  });
};

const userWorkplaceOptions = (user: any) => {
  if (user && user.email) {
    const getWorkplaceLists = getWorkplaceOptions(user?.configs);
    const ltiOptions: OhsltiWorkplace[] = [];

    ltiOptions.push({
      name: user.tier.name,
      tier: TierType.T3,
      id: user.tier._id,
    });

    getWorkplaceLists.forEach((list: any) => {
      ltiOptions.push({
        ...list,
        tier: TierType.T4,
      });
    });

    return ltiOptions;
  }
  return [];
};

const formatLtiLists = (incidentLtiLists: OhsIncidentLtiRecordResponse[]) => {
  return incidentLtiLists.map(({ _id, year, role, hours }: OhsIncidentLtiRecordResponse) => ({
    _id,
    year,
    role,
    hours,
  }));
};

// ------ LTI Edit payload -----

function formatLtiHoursPayload(data: OhsIncidentLtiRecord[]): TransformedLtiHoursData {
  const monthMap: { [key: string]: string } = {
    jan: 'Jan',
    feb: 'Feb',
    mar: 'Mar',
    apr: 'Apr',
    may: 'May',
    jun: 'Jun',
    jul: 'Jul',
    aug: 'Aug',
    sep: 'Sep',
    oct: 'Oct',
    nov: 'Nov',
    dec: 'Dec',
  };

  const transformed: TransformedLtiHoursData = {};

  data.forEach(({ year, role, ...months }) => {
    if (!transformed[year]) {
      transformed[year] = {};
    }

    if (role && !transformed[year][role]) {
      transformed[year][role] = {};
    }

    Object.entries(months).forEach(([month, value]) => {
      if (month !== 'id' && value !== null) {
        // @ts-ignore
        transformed[year][role][monthMap[month]] = Number(value);
      }
      if (value === '') {
        // @ts-ignore
        transformed[year][role][monthMap[month]] = 0;
      }
    });
  });

  return transformed;
}

// ----- LTI Edit payload -----

const genLtiFilter = (
  filter: OhsFilterList,
  selectedWp?: OhsltiWorkplace,
  roles?: string[],
  roleOptions?: string[],
  years?: string[],
  yearOptions?: string[]
) => {
  return {
    ...filter,
    incidentLtiModule: {
      ...filter.incidentLtiModule,
      ...(selectedWp && {
        currentWorkplace: selectedWp.id,
        targetTier: {
          _id: selectedWp.id,
          name: selectedWp.name,
          tier: selectedWp.tier,
        },
      }),
      ...(roles && { roles }),
      ...(roleOptions && { roleOptions }),
      ...(years && { years }),
      ...(yearOptions && { yearOptions }),
    },
  };
};

/*
The isValid function returns true if:
- The value is null, or
- The value is a number, is an integer, and is greater than or equal to 0.
*/
const isValidInteger = (value: any): boolean => {
  // Accept null as valid
  if (value === null) return true;
  // Otherwise, ensure the value is a number
  if (typeof value !== 'number') return false;
  // Reject non-integer values
  if (!Number.isInteger(value)) return false;
  // Accept only non-negative integers
  return value >= 0;
};

/**
 * Given an array of CREATE_INCIDENT_INJURED_PERSON_ROLE_LIST objects or strings, filter out
 * the role names that have ltiHoursCaptured === true and return an array of
 * strings. If the input is an array of strings, return the input array directly.
 */
function filterLtiIncidentRoles(userRoleList: IncidentInjuredPersonRoleList[]): string[] {
  let allowedRoles = [];

  if (userRoleList.length > 0 && typeof userRoleList[0] === 'object') {
    // userRoleList is an array of objects; filter for ltiHoursCaptured === true
    allowedRoles = userRoleList
      .filter((roleObj) => roleObj.options && roleObj.options.ltiHoursCaptured === true)
      .map((roleObj) => roleObj.role);
  } else {
    // Assume userRoleList is an array of strings.
    allowedRoles = userRoleList;
  }
  return allowedRoles as string[];
}

/**
 * Filter the incident list to include only records whose role is
 * in CREATE_INCIDENT_INJURED_PERSON_ROLE_LIST.
 * @returns {Array} - Filtered incidents.
 */
function filterIncidentList(incidentList: OhsIncidentLtiRecordResponse[], allowedRoles: string[]) {
  // Filter the incidentList to keep incidents whose role exists in allowedRoles.
  return incidentList.filter((incident) => allowedRoles.includes(incident.role));
}

/**
 * Ensures that for each distinct year in incidentLtilist, every role in roles
 * add records if missing.
 */
export function ensureRolesPerYear(
  incidentLtilist: OhsIncidentLtiRecordResponse[],
  roles: string[]
): OhsIncidentLtiRecordResponse[] {
  const years = Array.from(new Set(incidentLtilist.map((item) => item.year)));
  const filled: OhsIncidentLtiRecordResponse[] = [...incidentLtilist];

  years.forEach((year) => {
    roles.forEach((role) => {
      const roleName = role;
      if (!incidentLtilist.some((item) => item.year === year && item.role === roleName)) {
        const timestamp = Date.now();
        const newItem: any = {
          id: `${roleName}-${year}-${timestamp}`,
          year,
          role: roleName,
          jan: null,
          feb: null,
          mar: null,
          apr: null,
          may: null,
          jun: null,
          jul: null,
          aug: null,
          sep: null,
          oct: null,
          nov: null,
          dec: null,
        };
        // find last index of this year in filled array
        let lastIndex = -1;
        for (let i = 0; i < filled.length; i += 1) {
          if (filled[i].year === year) lastIndex = i;
        }
        // insert new item immediately after last original entry
        filled.splice(lastIndex + 1, 0, newItem);
      }
    });
  });

  return filled;
}

/**
 * Gets the oldest LTI lists based on current lists and target tier
 */
const getOldestLtiLists = async (
  currentIncidentLtiLists: OhsIncidentLtiRecordResponse[],
  ltiTargetTier: { _id: string; tier: TierType },
  incidentRoleList: string[]
): Promise<OhsIncidentLtiRecordResponse[]> => {
  try {
    // Get the oldest year from current lists
    const oldestYearCurrentIncidentLtiLists = currentIncidentLtiLists.reduce(
      (oldest: number, item) => {
        const year = Number(item.year);
        return year < oldest ? year : oldest;
      },
      Number.MAX_SAFE_INTEGER
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const fetchOldLtiRecord: any = (await OhsIncidentLtiServices.getIncidentOldestLtiLists({
      ...{
        _id: ltiTargetTier._id,
        type: ltiTargetTier.tier as TierType,
      },
    })) as unknown as OhsIncidentRecord;

    // Check if fetchOldLtiRecord has content
    if (!fetchOldLtiRecord || !fetchOldLtiRecord.dateOccurred) return [];

    const occurrenceDate = new Date(fetchOldLtiRecord.dateOccurred || new Date());
    const oldestYear = occurrenceDate.getFullYear();

    // if oldestYearCurrentIncidentLtiLists = 2020 and oldestYear = 2015
    // This generates records for missing years [2019, 2018, 2017, 2016, 2015]
    if (oldestYearCurrentIncidentLtiLists > oldestYear) {
      return genOldestYearList(oldestYearCurrentIncidentLtiLists, incidentRoleList, oldestYear);
    }

    return [];
  } catch (error) {
    console.error('Error getting oldest LTI lists:', error);
    return [];
  }
};

/**
 * Checks for year gaps in an array of LTI records.
 * Returns an array of missing years (descending order), or an empty array if no gaps.
 */
export function getMissingYearsFromRecords(records: { year: string | number }[]): number[] {
  const years = records.map((item) => Number(item.year)).filter((y) => !Number.isNaN(y));
  if (years.length === 0) return [];
  const minYear = Math.min(...years);
  //  const maxYear = Math.max(...years);
  const maxYear = moment().year(); // set 'latest year' to the current year
  const yearSet = new Set(years);
  const missingYears: number[] = [];
  for (let y = maxYear; y >= minYear; y -= 1) {
    if (!yearSet.has(y)) missingYears.push(y);
  }
  return missingYears;
}

function getDistinctYearsAndRoles(data: { year: string | number; role?: string }[]): {
  years: string[];
  roles: string[];
} {
  const yearsSet = new Set<string>();
  const rolesSet = new Set<string>();

  data.forEach((item) => {
    if (item.year !== undefined && item.year !== null) {
      yearsSet.add(String(item.year));
    }
    if (item.role) {
      rolesSet.add(item.role);
    }
  });

  return {
    // Convert Set to Array and sort numerically
    years: Array.from(yearsSet).sort((a, b) => Number(a) - Number(b)),
    roles: Array.from(rolesSet).sort(), // Convert Set to Array and sort
  };
}

const OhsIncidentLtiUtils = {
  genLast2yearsDefaultList,
  transformData,
  userWorkplaceOptions,
  formatLtiLists,
  formatLtiHoursPayload,
  genLtiFilter,
  isValidInteger,
  filterIncidentList,
  filterLtiIncidentRoles,
  ensureRolesPerYear,
  genOldestYearList,
  getOldestLtiLists,
  getMissingYearsFromRecords,
  genDefaultIncidentLtiLists,
  getDistinctYearsAndRoles,
};
export default OhsIncidentLtiUtils;
